import { useCallback, useMemo } from "react"
import {
  HeadingLarge,
  LabelLarge,
  Link,
  ParagraphLarge,
  useStyletron,
} from "@mezo-org/mezo-clay"

import { links } from "#/shared/constants"
import {
  useDonateMusd,
  useDonationAmountInputWithAmountPicker,
  useDonationDataById,
} from "#/shared/hooks"
import { floatToBigInt, trimAddress } from "#/shared/utils"
import { AmountInput } from "#/ui/Input"
import {
  MarketItemAmountPicker,
  MarketItemDetailsLayoutWithActionButton,
} from "#/ui/Market/MarketItemDetails/components"
import MarketDonationDetailsSuccess from "#/ui/Market/MarketItemDetails/MarketDonationDetailsSuccess"

export default function MarketDonationDetails({
  donationId,
}: {
  donationId: string
}) {
  const [, theme] = useStyletron()

  const donationData = useDonationDataById(donationId)

  const {
    donateMusd,
    isProcessing,
    isSuccess,
    hasError: transactionError,
    errorMessage: transactionErrorMessage,
    transactionHash,
  } = useDonateMusd()

  const { amount, isDonationAmountValid, amountPickerProps, inputProps } =
    useDonationAmountInputWithAmountPicker()

  const donateMusdCallback = useCallback(async () => {
    const beneficiaryId = donationData?.beneficiaryId

    if (!beneficiaryId || !amount) return

    await donateMusd(beneficiaryId, floatToBigInt(amount))
  }, [amount, donateMusd, donationData?.beneficiaryId])

  /**
   * Change any evm address occurrences in the details to link to Mezo explorer.
   */
  const detailsLinkified = useMemo(() => {
    const addressRegex = /(0x[a-fA-F0-9]{40})/
    if (!donationData || !donationData.details) return []
    return donationData?.details.split(addressRegex).map((part) => {
      if (addressRegex.test(part)) {
        return (
          <Link
            key={`link-${part}`}
            href={`${links.BLOCK_EXPLORER}/address/${part}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {trimAddress(part, 6, 4)}
          </Link>
        )
      }
      return part
    })
  }, [donationData])

  if (!donationData) return null

  if (isSuccess && transactionHash) {
    return (
      <MarketDonationDetailsSuccess
        transactionHash={transactionHash}
        image={donationData.fullImage}
        successMessage={donationData.successMessage}
      />
    )
  }

  return (
    <MarketItemDetailsLayoutWithActionButton
      itemData={donationData}
      submitButtonText={donationData.ctaButtonLabel}
      submitCallback={donateMusdCallback}
      submitButtonDataAttr={`${donationData.id}-contribute`}
      isSubmitButtonDisabled={!isDonationAmountValid}
      isProcessing={isProcessing}
      hasError={transactionError}
      errorMessage={transactionErrorMessage}
      termsOfUseNote={(link) => (
        <>
          By {donationData.id === "brink" ? "donating" : "contributing"}, you
          accept Mezo&apos;s {link}
        </>
      )}
    >
      <HeadingLarge marginBottom={theme.sizing.scale800}>
        {donationData.mainTitle}
      </HeadingLarge>
      <ParagraphLarge
        marginBottom={theme.sizing.scale600}
        overrides={{
          Block: {
            style: {
              textWrap: "pretty",
            },
          },
        }}
      >
        {donationData.mainDescription} Learn more at{" "}
        <Link href={donationData.learnMoreLink} target="_blank">
          {donationData.learnMoreLinkLabel}
        </Link>
        .
      </ParagraphLarge>
      <LabelLarge marginBottom={theme.sizing.scale600}>Details</LabelLarge>
      <ParagraphLarge
        marginBottom={theme.sizing.scale600}
        whiteSpace="pre-line"
      >
        {detailsLinkified}
      </ParagraphLarge>
      <LabelLarge marginBottom={theme.sizing.scale600}>
        Choose an amount
      </LabelLarge>
      <MarketItemAmountPicker {...amountPickerProps} />
      <AmountInput
        unitLabel="MUSD"
        value={amount}
        disabled={isProcessing}
        backgroundColor="#fff"
        fixedUnitLabel
        {...inputProps}
      />
    </MarketItemDetailsLayoutWithActionButton>
  )
}
